import React, { useState, useEffect } from "react";
import { createAppointment } from '../../Services/appointmentService';
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { useMasterData } from "../Common/commonEvents";

export default function AppointmentModal({ show, onClose, onSuccess }) {

    const [appointmentType, setAppointmentType] = useState("NEW");
    const initialFormData = {
        name: "",
        mobile: "",
        email: "",
        dob: "",
        age: "",
        gender: "",
        address: "",
        unitId: "",
        appointmentDate: "",
        notes: "",
        allergy: "",
        bp: "",
        weight: "",
        diabetes: false,
        epilepsy: false,
        asthma: false,
        otherConditions: "",
        medications: "",
        slotTime: "",
        procedureId: "",
        doctorId: "",
    };
    
    const [formData, setFormData] = useState(initialFormData);

    const sessionUserData = JSON.parse(sessionStorage.getItem("userData") || "{}");

    const [loading, setLoading] = useState(false);
    const [doctorId, setDoctorId] = useState("");
    const [timeSlot, setTimeSlot] = useState("");
    const [selectedUnitId, setSelectedUnitId] = useState(sessionUserData?.UnitId);

    const {
        procedures,
        usersByRole,
        doctorSlotsByDate,
        units,
        fetchUsersByRole,
        fetchDoctorSlotsForDate,
        fetchProcedureMasters,
        fetchUnits,
    } = useMasterData();

    const filteredDoctors = (usersByRole || []).filter(
        (doctor) => doctor.UnitId == selectedUnitId
    );

    useEffect(() => {
        if (!show) return;
        if (!sessionUserData?.OrgId || !sessionUserData?.UnitId) return;

        fetchUsersByRole(sessionUserData.OrgId, 2); // doctors
        fetchProcedureMasters(sessionUserData.OrgId, sessionUserData.UnitId);
        fetchUnits(sessionUserData.OrgId);
    }, [show, sessionUserData?.OrgId, sessionUserData?.UnitId]);

    useEffect(() => {
        if (!sessionUserData?.OrgId || !doctorId) return;

        fetchDoctorSlotsForDate(sessionUserData.OrgId, doctorId, formData?.appointmentDate); // doctor slots
    }, [sessionUserData?.OrgId, doctorId, formData?.appointmentDate, fetchDoctorSlotsForDate]);

    if (!show) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
    
        if (name === "dob") {
            let calculatedAge = "";
    
            if (value) {
                calculatedAge = dayjs().diff(dayjs(value), "year");
    
                if (calculatedAge < 0) calculatedAge = 0;
            }
    
            setFormData((prev) => ({
                ...prev,
                dob: value,
                age: calculatedAge,
            }));
            return;
        }
    
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const Toast = Swal.mixin({
        toast: true,
        position: "top",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
    });

    const formatApiTime = (isoString, formatType) => {
        const timePart = isoString.split("T")[1].substring(0, 5); // "17:30"
        return dayjs(timePart, "HH:mm").format(formatType);
    };

    const availableSlots = (doctorSlotsByDate || [])
        .filter(slot => slot.IsBooked === 0 && slot.SlotStatus === "Available")
        .map(slot => ({
            label: formatApiTime(slot.SlotStart, "hh:mm A"), // Displays: "05:30 PM"
            value: formatApiTime(slot.SlotStart, "HH:mm"),   // Target dropdown key value: "17:30"
        }));

    const showRequiredToast = (title) => {
        Toast.fire({
            icon: "warning",
            title,
        });
    };

    const validateRequiredFields = () => {
        const commonRequiredFields = [
            { value: formData.mobile, message: "Please enter mobile number" },
            { value: selectedUnitId, message: "Please select branch" },
            { value: formData.appointmentDate, message: "Please select appointment date" },
            { value: doctorId, message: "Please select doctor" },
            { value: timeSlot, message: "Please select time slot" },
        ];
    
        const newPatientRequiredFields = [
            { value: formData.name, message: "Please enter patient name" },
            { value: formData.dob, message: "Please select date of birth" },
            { value: formData.age, message: "Please enter age" },
        ];

        if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
            showRequiredToast("Please enter a valid 10-digit mobile number");
            return false;
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email)) {
            showRequiredToast("Please enter a valid email address");
            return false;
        }
    
        if (appointmentType === "NEW") {
            const name = formData.name?.trim() || "";
    
            if (name.length < 3) {
                showRequiredToast("Patient name must be at least 3 characters");
                return false;
            }
    
            if (name.length > 40) {
                showRequiredToast("Patient name cannot exceed 40 characters");
                return false;
            }
    
            if (!/^[A-Za-z.\s]+$/.test(name)) {
                showRequiredToast(
                    "Patient name can contain only letters, spaces and dot (.)"
                );
                return false;
            }
    
            if (!/[A-Za-z]/.test(name)) {
                showRequiredToast("Patient name cannot contain only numbers");
                return false;
            }
        }
    
        const requiredFields =
            appointmentType === "NEW"
                ? [...newPatientRequiredFields, ...commonRequiredFields]
                : commonRequiredFields;
    
        const missingField = requiredFields.find(
            (field) =>
                field.value === undefined ||
                field.value === null ||
                String(field.value).trim() === ""
        );
    
        if (missingField) {
            showRequiredToast(missingField.message);
            return false;
        }
    
        return true;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateRequiredFields()) return;

        const basePayload = {
            OrgId: sessionUserData?.OrgId,
            UserId: sessionUserData?.Id,
            Type: appointmentType,
        };

        const payload =
            appointmentType === "EXISTING"
                ? {
                    ...basePayload,
                    JsonData: {
                        Mobile: formData.mobile,
                        UnitId: Number(selectedUnitId),
                        AppointmentDate: formData.appointmentDate,
                        SlotTime: timeSlot,
                        DoctorId: Number(doctorId),
                        ProcedureId: formData.procedureId,
                    },
                }
                : {
                    ...basePayload,
                    JsonData: {
                        Name: formData.name,
                        Mobile: formData.mobile,
                        Email: formData.email,
                        DOB: formData.dob,
                        Age: Number(formData.age) || 0,
                        Gender: Number(formData.gender),
                        Address: formData.address,
                        UnitId: selectedUnitId,
                        AppointmentDate: formData.appointmentDate,
                        Notes: formData.notes,
                        Allergy: formData.allergy || "None",
                        BP: formData.bp,
                        Weight: formData.weight,
                        Diabetes: formData.diabetes ? 1 : 0,
                        Epilepsy: formData.epilepsy ? 1 : 0,
                        Asthma: formData.asthma ? 1 : 0,
                        OtherConditions: formData.otherConditions,
                        Medications: formData.medications,
                        SlotTime: timeSlot,
                        DoctorId: Number(doctorId),
                        ProcedureId: formData.procedureId,
                    },
                };

        try {
            setLoading(true);

            const response = await createAppointment(payload);

            const result = response?.data?.result?.[0];

            if (response?.success && result?.ResponseCode === 2001) {

                Toast.fire({
                    icon: "success",
                    title: result?.Message || "Appointment created successfully",
                });
            
                setFormData(initialFormData);
            
                onSuccess?.();
                onClose();
            } else {
                Toast.fire({
                    icon: "error",
                    title: result?.Message || response?.message || "Failed to create appointment",
                });
            }
        } catch (error) {
            console.error(error.response?.data || error.message);

            Toast.fire({
                icon: "error",
                title:
                    error.response?.data?.message ||
                    "Failed to create appointment",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="modal show d-block"
            style={{
                background: "rgba(15, 23, 42, 0.62)",
                backdropFilter: "blur(6px)",
                overflowY: "auto",
                padding: "24px 12px",
            }}
        >
            <div
                className="modal-dialog modal-xl modal-dialog-centered"
                style={{
                    maxHeight: "calc(100vh - 48px)",
                    margin: "0 auto",
                }}
            >
                <div
                    className="modal-content border-0 shadow-lg overflow-hidden"
                    style={{
                        borderRadius: "22px",
                        maxHeight: "calc(100vh - 48px)",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div
                        className="modal-header border-0 px-4 py-4"
                        style={{
                            background: "linear-gradient(135deg, #0f766e 0%, #2563eb 100%)",
                        }}
                    >
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center"
                                style={{ width: "52px", height: "52px" }}
                            >
                                <i className="bi bi-calendar2-plus text-white fs-3"></i>
                            </div>

                            <div>
                                <h5 className="modal-title fw-bold text-white mb-1">
                                    Make Appointment
                                </h5>
                                <div className="text-white text-opacity-75 fs-7">
                                    Add patient details and schedule slot
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-sm rounded-circle bg-white bg-opacity-25 border-0 text-white"
                            onClick={onClose}
                            style={{ width: "38px", height: "38px" }}
                        >
                            <i className="bi bi-x-lg text-white"></i>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <div
                            className="modal-body p-4 bg-light"
                            style={{
                                overflowY: "auto",
                                flex: "1 1 auto",
                            }}
                        >
                            <div className="bg-white rounded-4 p-3 shadow-sm mb-4">
                                <label className="form-label fw-bold text-dark mb-2">Patient Type</label>

                                <div className="d-flex gap-3 flex-wrap">
                                    <label
                                        className={`border rounded-4 px-4 py-3 d-flex align-items-center gap-3 ${appointmentType === "NEW"
                                            ? "border-primary bg-primary bg-opacity-10"
                                            : "bg-light border-light"
                                            }`}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <input
                                            type="radio"
                                            name="appointmentType"
                                            value="NEW"
                                            checked={appointmentType === "NEW"}
                                            onChange={(e) => setAppointmentType(e.target.value)}
                                            className="form-check-input m-0"
                                        />
                                        <i className="bi bi-person-plus text-primary fs-5"></i>
                                        <span className="fw-bold">New Patient</span>
                                    </label>

                                    <label
                                        className={`border rounded-4 px-4 py-3 d-flex align-items-center gap-3 ${appointmentType === "EXISTING"
                                            ? "border-success bg-success bg-opacity-10"
                                            : "bg-light border-light"
                                            }`}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <input
                                            type="radio"
                                            name="appointmentType"
                                            value="EXISTING"
                                            checked={appointmentType === "EXISTING"}
                                            onChange={(e) => setAppointmentType(e.target.value)}
                                            className="form-check-input m-0"
                                        />
                                        <i className="bi bi-person-check text-success fs-5"></i>
                                        <span className="fw-bold">Existing Patient</span>
                                    </label>
                                </div>
                            </div>

                            <div className="bg-white rounded-4 p-4 shadow-sm mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-person-vcard text-primary fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">
                                        {appointmentType === "NEW" ? "Patient Information" : "Existing Patient"}
                                    </h6>
                                </div>
                                <div className="row g-3">
                                    {appointmentType === "NEW" && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Name<span className="text-danger">*</span></label>
                                            <input
    name="name"
    className="form-control form-control-sm rounded-3 border-light bg-light"
    value={formData.name}
    onChange={(e) => {
        let value = e.target.value;

        if (/^[A-Za-z.\s]*$/.test(value) && value.length <= 40) {

            value = value.toLowerCase().replace(
                /(^\w)|(\s+\w)|(\.\s*\w)/g,
                (match) => match.toUpperCase()
            );

            handleChange({
                target: {
                    name: "name",
                    value
                }
            });
        }
    }}
    placeholder="Enter patient name"
    maxLength={40}
/>
                                        </div>
                                    )}
                                   <div className="col-md-4">
    <label className="form-label fw-semibold text-muted">
        Mobile<span className="text-danger">*</span>
    </label>

    <div className="input-group input-group-sm">
        <span className="input-group-text bg-light border-light">
            🇮🇳 +91
        </span>

        <input
            type="tel"
            name="mobile"
            className="form-control border-light bg-light"
            value={formData.mobile}
            onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");

                if (value.length <= 10) {
                    handleChange({
                        target: {
                            name: "mobile",
                            value
                        }
                    });
                }
            }}
            placeholder="Enter mobile number"
            maxLength={10}
            required
        />
    </div>
</div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Enter email address"
                                        />
                                    </div>
                                    {appointmentType === "NEW" && (
                                        <>
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold text-muted">Gender<span className="text-danger">*</span></label>
                                                <select
                                                    name="gender"
                                                    className="form-select form-select-sm rounded-3 border-light bg-light"
                                                    value={formData.gender}
                                                    onChange={handleChange}
                                                >
                                                    <option value="1">Male</option>
                                                    <option value="2">Female</option>
                                                    <option value="3">Other</option>
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold text-muted">DOB<span className="text-danger">*</span></label>
                                                <input
                                                    type="date"
                                                    name="dob"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.dob}
                                                    onChange={handleChange}
                                                    max={dayjs().subtract(1, 'year').format('YYYY-MM-DD')}
                                                    required={appointmentType === "NEW"}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold text-muted">Age<span className="text-danger">*</span></label>
                                                <input
                                                    type="number"
                                                    name="age"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.age || ""}
                                                    onChange={handleChange}
                                                    placeholder="Age"
                                                />
                                            </div>

                                            <div className="col-md-12">
                                                <label className="form-label fw-semibold text-muted">Address<span className="text-danger">*</span></label>
                                                <textarea
                                                    name="address"
                                                    className="form-control rounded-3 border-light bg-light"
                                                    rows="2"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    placeholder="Enter address"
                                                    required={appointmentType === "NEW"}
                                                ></textarea>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white rounded-4 p-4 shadow-sm mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-calendar-check text-success fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">Appointment Details</h6>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-4 col-12">
                                        <label className="form-label fw-semibold text-muted">Procedure</label>
                                        <select
                                            className="form-select form-select-sm"
                                            name="procedureId"
                                            value={formData.procedureId}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Procedure</option>

                                            {procedures.map((procedure) => {
                                                const id = procedure.ProcedureId || procedure.Id;
                                                const name = procedure.ProcedureName || procedure.Name;

                                                return (
                                                    <option key={id} value={id}>
                                                        {name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    <div className="col-md-2 col-12">
                                        <label className="form-label fw-semibold text-muted">Appointment Date<span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            name="appointmentDate"
                                            className="form-control form-control-sm rounded-3 border-secondary shadow-sm"
                                            value={formData.appointmentDate}
                                            onChange={handleChange}
                                            // min={dayjs().format("YYYY-MM-DD")}
                                            required
                                        />
                                    </div>
                                    {sessionUserData?.RoleId === 1 && (
                                        <div className="col-12 col-md-6 col-xl-3">
                                            <label className="form-label fw-semibold text-muted ">
                                                Branch
                                            </label>
                                            <select
                                                name="status"
                                                className="form-select form-select-sm rounded-3"
                                                value={selectedUnitId}
                                                onChange={(e) => setSelectedUnitId(e.target.value)}
                                            >
                                                {units && units?.map((unit => (
                                                    <option key={unit.UnitId} value={unit.UnitId}>{unit.UnitName}</option>
                                                )))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="col-12 col-md-3">
                                        <label className="form-label fw-semibold text-muted">
                                            Select Doctor<span className="text-danger">*</span>
                                        </label>

                                        <select
                                            className="form-select form-select-sm"
                                            value={doctorId}
                                            onChange={(e) => setDoctorId(e.target.value)}
                                            required
                                        >
                                            <option value="">Choose a doctor...</option>
                                            {filteredDoctors.length > 0 ? (
                                                filteredDoctors.map((doctor) => (
                                                    <option key={doctor.Id} value={doctor.Id}>
                                                        {doctor.Name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="" disabled>
                                                    ✕ No doctors available for your unit
                                                </option>
                                            )}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <label className="form-label text-muted fw-semibold">
                                            Select Time Slot<span className="text-danger">*</span>
                                        </label>

                                        <div className="dropdown w-100">
                                            <button
                                                className="form-select premium-select-trigger text-start w-100 d-flex justify-content-between align-items-center"
                                                type="button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                <span>
                                                    {timeSlot
                                                        ? availableSlots.find(s => s.value === timeSlot)?.label || "Selected Slot"
                                                        : "Choose a slot..."}
                                                </span>
                                            </button>

                                            <ul className="dropdown-menu premium-scroll-dropdown w-100 py-1">
                                                {availableSlots.length > 0 ? (
                                                    availableSlots.map((slot, index) => (
                                                        <li key={index}>
                                                            <button
                                                                type="button"
                                                                className={`dropdown-item slot-menu-item ${timeSlot === slot.value ? 'active' : ''}`}
                                                                onClick={() => setTimeSlot(slot.value)}
                                                            >
                                                                {slot.label}
                                                            </button>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li className="px-3 py-2 text-muted small text-center">
                                                        ✕ No slots available for this day
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold text-muted">Notes</label>
                                        <textarea
                                            name="notes"
                                            className="form-control rounded-3 border-light bg-light"
                                            rows="2"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Reason for visit"
                                        ></textarea>
                                    </div>
                                </div>
                            </div>

                            {appointmentType === "NEW" && (
                                <div className="bg-white rounded-4 p-4 shadow-sm">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-heart-pulse text-danger fs-5"></i>
                                        <h6 className="fw-bold mb-0 text-dark">Medical History</h6>
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Allergy</label>
                                            <textarea
                                                name="allergy"
                                                className="form-control rounded-3 border-light bg-light"
                                                rows="2"
                                                value={formData.allergy}
                                                onChange={handleChange}
                                                placeholder="None"
                                            ></textarea>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Other Conditions</label>
                                            <textarea
                                                name="otherConditions"
                                                className="form-control rounded-3 border-light bg-light"
                                                rows="2"
                                                value={formData.otherConditions}
                                                onChange={handleChange}
                                                placeholder="Mention conditions"
                                            ></textarea>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Medications</label>
                                            <textarea
                                                name="medications"
                                                className="form-control rounded-3 border-light bg-light"
                                                rows="2"
                                                value={formData.medications}
                                                onChange={handleChange}
                                                placeholder="Current medications"
                                            ></textarea>
                                        </div>

                                        <div className="row align-items-end g-3">
                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold text-muted">Weight</label>
                                                <input
                                                    name="weight"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.weight}
                                                    onChange={handleChange}
                                                    placeholder="65 kgs"
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold text-muted">BP</label>
                                                <input
                                                    name="bp"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.bp}
                                                    onChange={handleChange}
                                                    placeholder="120/80"
                                                />
                                            </div>

                                            <div className="col-md-8">
                                                <div className="d-flex flex-wrap gap-3">

                                                    {[
                                                        ["diabetes", "Diabetes", "bi-droplet-half"],
                                                        ["epilepsy", "Epilepsy", "bi-activity"],
                                                        ["asthma", "Asthma", "bi-lungs"],
                                                    ].map(([name, label, icon]) => (
                                                        <label
                                                            key={name}
                                                            className={`border rounded-4 px-4 py-2 d-flex align-items-center gap-3 ${formData[name]
                                                                ? "border-primary bg-primary bg-opacity-10"
                                                                : "bg-light border-light"
                                                                }`}
                                                            style={{
                                                                cursor: "pointer",
                                                                minWidth: "170px",
                                                                height: "42px",
                                                            }}
                                                        >
                                                            <input
    className="form-check-input m-0"
    type="checkbox"
    name={name}
    checked={formData[name] || false}
    onChange={handleChange}
/>

                                                            <i
                                                                className={`bi ${icon} text-primary fs-5`}
                                                            ></i>

                                                            <span className="fw-semibold text-dark">
                                                                {label}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer border-0 bg-white px-4 py-3 flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-light fw-semibold rounded-3 px-4 py-2"
                                onClick={onClose}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary fw-bold rounded-3 px-4 py-2 shadow-sm"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-calendar2-check me-2"></i>
                                        {appointmentType === "NEW" ? "Make Appointment" : "Book Existing Patient"}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>
                {`
                /* Container rules establishing the explicit scroll zone limits */
                    .premium-scroll-dropdown {
                        max-height: 200px !important;
                        overflow-y: auto !important;
                        border: 1px solid #e2e8f0 !important;
                        border-radius: 8px !important;
                        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05) !important;
                    }

                    /* Custom Scrollbar configuration for the dropdown box container */
                    .premium-scroll-dropdown::-webkit-scrollbar {
                        width: 6px !important;
                    }

                    .premium-scroll-dropdown::-webkit-scrollbar-track {
                        background: #f8fafc !important; /* Slate 50 */
                        border-radius: 0 8px 8px 0;
                    }

                    .premium-scroll-dropdown::-webkit-scrollbar-thumb {
                        background: #cbd5e1 !important; /* Slate 300 */
                        border-radius: 4px !important;
                    }

                    .premium-scroll-dropdown::-webkit-scrollbar-thumb:hover {
                        background: #94a3b8 !important; /* Slate 400 */
                    }

                    /* Matching the trigger button to your standard select inputs layout */
                    .premium-select-trigger {
                        padding: 0.55rem 1rem !important;
                        font-size: 0.9rem !important;
                        color: #0f172a !important;
                        background-color: #ffffff !important;
                        border: 1px solid #e2e8f0 !important;
                        border-radius: 8px !important;
                        cursor: pointer;
                    }

                    .premium-select-trigger::after {
                        margin-left: auto; /* Correctly pushes the caret arrow layout to the right edge */
                    }

                    /* Dropdown individual clickable row configurations */
                    .slot-menu-item {
                        font-size: 0.875rem !important;
                        padding: 0.5rem 1rem !important;
                        color: #334155 !important;
                        transition: all 0.15s ease;
                    }

                    .slot-menu-item:hover {
                        background-color: #f1f5f9 !important; /* Slate 100 */
                        color: #0f172a !important;
                    }

                    /* Selected layout status */
                    .slot-menu-item.active {
                        background-color: #0f172a !important; /* Premium dark active state theme */
                        color: #ffffff !important;
                    }
                `}
            </style>
        </div>
    );
}
